import { nativeDocumentText, renderContentDocument } from './renderer.mjs';

const canvasOnlyNote = block => block.flavour === 'affine:note' && (block.props.displayMode === 'edgeless' || block.props.hidden === true);

/** A blank paragraph added by page mode is an editing affordance, not reader content. */
export function hasDocumentContent(snapshot) {
  const visit = block => {
    if (['affine:surface', 'affine:edgeless-text', 'affine:frame'].includes(block.flavour) || canvasOnlyNote(block)) return false;
    if (block.flavour === 'affine:image' || block.flavour === 'affine:divider') return true;
    if (block.flavour !== 'affine:page' && nativeDocumentText(block.props).length) return true;
    if (block.flavour === 'affine:latex' && typeof block.props.latex === 'string' && block.props.latex.trim()) return true;
    return block.children.some(visit);
  };
  return Boolean(snapshot?.blocks && visit(snapshot.blocks));
}

/** Page mode omits surface elements and blocks placed only on the canvas. */
export function hasCanvasContent(snapshot) {
  const visit = block => {
    if (block.flavour === 'affine:surface' && (Object.keys(block.props.elements || {}).length || block.children.length)) return true;
    if (block.flavour === 'affine:edgeless-text' || block.flavour === 'affine:frame') return true;
    if (canvasOnlyNote(block)) return true;
    return block.children.some(visit);
  };
  return Boolean(snapshot?.blocks && visit(snapshot.blocks));
}

/** Bind semantic learning actions to the original exported design, without a second editor. */
function hydrateLearning(root, content) {
  let disposed = false;
  let quizModule;
  const cleanups = [];
  const quizzes = [];
  const listen = (element, event, callback) => {
    element.addEventListener(event, callback);
    cleanups.push(() => element.removeEventListener(event, callback));
  };
  for (const section of content.sections) {
    if (section.kind !== 'penpot' || !section.preview || !section.learning?.length) continue;
    const host = [...root.querySelectorAll('[data-section-id]')].find(node => node.dataset.sectionId === section.id);
    if (!host) continue;
    for (const item of section.learning) {
      const card = [...host.querySelectorAll('[data-learning-item]')].find(node => node.dataset.learningItem === item.id);
      const hotspot = [...host.querySelectorAll('[data-learning-hotspot]')].find(node => node.dataset.learningHotspot === item.id);
      if (!card || !hotspot) continue;
      const video = card.querySelector('[data-learning-video]');
      const quizHost = card.querySelector('[data-learning-quiz]');
      let quizLoading = false;
      let quizMounted = false;
      const close = () => {
        card.open = false;
        hotspot.setAttribute('aria-expanded', 'false');
        if (video) video.pause();
        hotspot.focus({ preventScroll: true });
        hotspot.scrollIntoView({ block: 'nearest' });
      };
      listen(hotspot, 'click', () => {
        card.open = true;
        hotspot.setAttribute('aria-expanded', 'true');
        card.querySelector('summary').focus({ preventScroll: true });
        card.scrollIntoView({ block: 'nearest' });
      });
      listen(card.querySelector('[data-learning-close]'), 'click', close);
      listen(card, 'keydown', event => {
        if (event.key === 'Escape' && card.open) { event.preventDefault(); close(); }
      });
      if (video) {
        const status = card.querySelector('[data-video-status]');
        const announce = (text, error = false) => {
          status.textContent = text;
          status.setAttribute('role', error ? 'alert' : 'status');
        };
        listen(video, 'loadstart', () => announce(video.paused ? '재생 버튼을 눌러 영상을 시작하세요.' : '영상을 불러오고 있습니다.'));
        listen(video, 'play', () => announce('영상을 불러오고 있습니다.'));
        listen(video, 'loadedmetadata', () => announce(video.paused ? '재생 버튼을 눌러 영상을 시작하세요.' : '영상을 불러오고 있습니다.'));
        listen(video, 'playing', () => announce('영상을 재생하고 있습니다.'));
        listen(video, 'waiting', () => announce('영상 데이터를 기다리고 있습니다.'));
        listen(video, 'ended', () => announce('영상을 모두 시청했습니다.'));
        listen(video, 'error', () => announce('영상을 재생할 수 없습니다. 영상 주소와 지원 형식을 확인해 주세요.', true));
        cleanups.push(() => { video.pause(); video.removeAttribute('src'); video.load(); });
      }
      listen(card, 'toggle', async () => {
        if (disposed) return;
        hotspot.setAttribute('aria-expanded', String(card.open));
        if (!card.open) { if (video) video.pause(); return; }
        if (video && !video.hasAttribute('src')) video.src = item.url;
        if (!quizHost || quizLoading || quizMounted) return;
        quizLoading = true;
        const loading = document.createElement('p');
        loading.setAttribute('role', 'status');
        loading.textContent = '문제를 불러오고 있습니다.';
        quizHost.replaceChildren(loading);
        try {
          quizModule ??= import('./learning-quiz.js');
          const { mountQuiz } = await quizModule;
          if (disposed) return;
          const session = mountQuiz(quizHost, item);
          quizzes.push(session); quizMounted = true;
          quizHost.dataset.loaded = 'true';
        } catch {
          if (disposed) return;
          quizModule = null;
          quizHost.replaceChildren();
          const alert = document.createElement('p');
          alert.setAttribute('role', 'alert');
          alert.textContent = '문제를 불러오지 못했습니다. 페이지를 다시 열어 주세요.';
          quizHost.append(alert);
        } finally { quizLoading = false; }
      });
    }
  }
  return { dispose() {
    if (disposed) return;
    disposed = true;
    cleanups.forEach(cleanup => cleanup());
    quizzes.forEach(session => session.dispose());
  } };
}

/** Hydrate the validated source only. Async mounts are disposed on navigation. */
export function hydrateContentDocument(root, content) {
  let disposed = false;
  const sessions = [hydrateLearning(root, content)];
  const supplements = [];
  const ready = (async () => {
    if (![2, 3].includes(content.schemaVersion) || content.sections.every(section => section.kind === 'penpot')) return;
    const { mount } = await import('./blocksuite-engine.mjs');
    for (const section of content.sections) {
      if (disposed) return;
      if (section.kind === 'penpot') continue;
      const host = [...root.querySelectorAll('[data-native-section]')].find(node => node.dataset.nativeSection === section.id);
      if (!host) continue;
      const transcript = host.nextElementSibling;
      const supplementalCanvas = section.kind === 'document' && hasCanvasContent(section.snapshot);
      try {
        const session = await mount(host, { mode: section.kind === 'canvas' ? 'edgeless' : 'page', snapshot: section.snapshot, assets: section.assets, readonly: true });
        if (disposed) { session.dispose(); return; }
        sessions.push(session);
        host.dataset.loaded = 'true';
        if (section.kind === 'document' && !supplementalCanvas) transcript.hidden = true;
      } catch (error) {
        if (disposed) return;
        const alert = document.createElement('p');
        alert.setAttribute('role', 'alert');
        alert.textContent = `편집 화면을 열지 못했습니다. 아래 원문은 계속 읽을 수 있습니다. ${error.message}`;
        host.after(alert); supplements.push(alert);
        transcript.hidden = false;
      }
    }
  })();
  ready.catch(error => {
    if (disposed) return;
    const alert = document.createElement('p');
    alert.setAttribute('role', 'alert');
    alert.textContent = `콘텐츠 편집 엔진을 불러오지 못했습니다. ${error.message}`;
    root.prepend(alert); supplements.push(alert);
  });
  return { ready, dispose() {
    if (disposed) return;
    disposed = true; sessions.forEach(session => session.dispose());
    supplements.forEach(node => node.remove());
  } };
}

export function mountContentReader(root, content) {
  root.innerHTML = renderContentDocument(content);
  return hydrateContentDocument(root, content);
}
